'use client'

import { useState, type SetStateAction } from 'react'

import { cn } from '@/lib/utils'
import { useSearchTracking } from '@/hooks/use-search-tracking'
import { useWidgetState, type WidgetState } from '@/hooks/use-widget-state'

import { LocationCombobox } from './location-combobox'
import { SearchDatePicker } from './search-date-picker'

export type WidgetStateTuple = readonly [WidgetState, (action: SetStateAction<WidgetState>) => void]

interface SearchWidgetProps {
    initialFrom?: string
    initialFromId?: string
    initialTo?: string
    initialToId?: string
    initialMode?: string
    initialDate?: string
    compact?: boolean
    /** Controlled state — when provided, widget uses this instead of internal state */
    widgetState?: WidgetStateTuple
    /** Unique suffix for radio button names to avoid conflicts with multiple instances */
    formId?: string
    staticBaseUrl?: string
}

interface ValidationErrors {
    from?: boolean
    to?: boolean
    departDate?: boolean
}

export function SearchWidget({
    initialFrom,
    initialFromId,
    initialTo,
    initialToId,
    initialMode = 'bus',
    initialDate,
    compact = false,
    widgetState,
    formId,
    staticBaseUrl,
}: SearchWidgetProps) {
    const internalState = useWidgetState({
        from: initialFrom,
        fromId: initialFromId,
        to: initialTo,
        toId: initialToId,
        mode: initialMode as 'bus' | 'train' | 'ferry' | 'flight',
        departDate: initialDate ?? '',
    })
    const [state, setState] = widgetState ?? internalState
    const radioName = formId ? `tripType-${formId}` : 'tripType'

    const [errors, setErrors] = useState<ValidationErrors>({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const tracking = useSearchTracking(initialMode)
    const [swapRotation, setSwapRotation] = useState(0)

    const handleSwap = () => {
        setSwapRotation((r) => r - 180)
        setState((s) => ({
            ...s,
            from: s.to,
            fromId: s.toId,
            to: s.from,
            toId: s.fromId,
        }))
        setErrors((e) => ({ ...e, from: false, to: false }))
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()

        const newErrors: ValidationErrors = {
            from: !state.from || !state.fromId,
            to: !state.to || !state.toId,
            departDate: !state.departDate,
        }

        // Same-location check
        if (state.fromId && state.toId && state.fromId === state.toId) {
            newErrors.from = true
            newErrors.to = true
        }

        if (newErrors.from || newErrors.to || newErrors.departDate) {
            setErrors(newErrors)
            const errorFields = Object.entries(newErrors)
                .filter(([, v]) => v)
                .map(([k]) => k)
            tracking.trackValidationError(errorFields)
            return
        }

        setErrors({})
        setIsSubmitting(true)
        tracking.trackSubmit(state.from, state.to, state.departDate)

        const from = encodeURIComponent(state.from)
        const to = encodeURIComponent(state.to)
        let url = `/depart/${from}/${to}/${state.departDate}/`

        if (state.tripType === 'round-trip' && state.returnDate) {
            url += `${state.returnDate}/`
        }

        const params = new URLSearchParams()
        if (state.fromId) {
            params.set('opid', state.fromId)
        }
        if (state.toId) {
            params.set('dpid', state.toId)
        }
        const qs = params.toString()
        if (qs) {
            url += `?${qs}`
        }

        window.location.href = url
    }

    const isOneWay = state.tripType === 'one-way'

    return (
        <form
            onSubmit={handleSubmit}
            className={cn('rounded-lg', compact ? 'flex items-end gap-2' : '')}
            data-testid="search-widget"
            data-section="search"
        >
            {/* Trip type toggle */}
            {!compact && (
                <div className="mb-4 flex gap-6">
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-[#2e3033]">
                        <span
                            className={cn(
                                'flex h-5 w-5 items-center justify-center rounded-full border-2',
                                isOneWay ? 'border-[#188da1]' : 'border-[#b0b5bc]',
                            )}
                        >
                            {isOneWay && <span className="h-2.5 w-2.5 rounded-full bg-[#188da1]" />}
                        </span>
                        <input
                            type="radio"
                            name={radioName}
                            value="one-way"
                            checked={isOneWay}
                            onChange={() => {
                                setState((s) => ({ ...s, tripType: 'one-way' }))
                            }}
                            className="sr-only"
                        />
                        One way
                    </label>
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-[#2e3033]">
                        <span
                            className={cn(
                                'flex h-5 w-5 items-center justify-center rounded-full border-2',
                                !isOneWay ? 'border-[#188da1]' : 'border-[#b0b5bc]',
                            )}
                        >
                            {!isOneWay && (
                                <span className="h-2.5 w-2.5 rounded-full bg-[#188da1]" />
                            )}
                        </span>
                        <input
                            type="radio"
                            name={radioName}
                            value="round-trip"
                            checked={!isOneWay}
                            onChange={() => {
                                setState((s) => ({ ...s, tripType: 'round-trip' }))
                            }}
                            className="sr-only"
                        />
                        Round trip
                    </label>
                </div>
            )}

            {/* Form fields */}
            <div
                className={cn(
                    'relative',
                    compact
                        ? 'flex flex-1 items-end gap-2'
                        : cn(
                              'grid gap-2',
                              isOneWay
                                  ? 'md:grid-cols-[1fr_auto_1fr_1fr_auto]'
                                  : 'md:grid-cols-[1fr_auto_1fr_2fr_auto]',
                          ),
                )}
            >
                {/* Chiku mascot — md breakpoint only (lg+ uses desktop variant in header) */}
                {staticBaseUrl && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        alt=""
                        className="absolute top-[-62px] right-[10px] z-10 hidden md:block lg:hidden"
                        height={66}
                        src={`${staticBaseUrl}c2c/chiku-mobile.svg`}
                        width={82}
                    />
                )}

                {/* All fields grouped in one card on mobile, inline grid items on desktop */}
                <div className="relative overflow-hidden rounded-[7px] border border-[#d9dee5] md:contents">
                    {/* From */}
                    <LocationCombobox
                        label="From"
                        value={state.from}
                        onChange={(value, placeId) => {
                            setState((s) => ({ ...s, from: value, fromId: placeId }))
                            setErrors((e) => ({ ...e, from: false }))
                            if (placeId) {
                                tracking.trackPredictionSelected('from', value)
                            }
                        }}
                        placeholder="City or station"
                        hasError={errors.from}
                        grouped
                    />

                    {/* Swap — overlaid circle on mobile, inline rounded-rect on desktop */}
                    <div className="absolute top-12 right-4 z-10 -translate-y-1/2 md:static md:flex md:translate-y-0 md:items-center md:justify-center">
                        <button
                            type="button"
                            onClick={handleSwap}
                            aria-label="Swap locations"
                            className="flex h-12 w-12 rotate-90 items-center justify-center rounded-full border border-[#d9dee5] bg-white text-[#8c939c] transition-colors hover:bg-[#f5f6f7] md:rotate-0 md:rounded-[7px]"
                        >
                            <svg
                                className="h-6 w-6 transition-transform duration-300 ease-in-out"
                                style={{ transform: `rotate(${swapRotation}deg)` }}
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                aria-hidden="true"
                                focusable="false"
                            >
                                <path d="M23.7071 8.70714C24.0976 8.31661 24.0976 7.68345 23.7071 7.29292L19.7071 3.29292C19.4211 3.00692 18.991 2.92137 18.6173 3.07615C18.2436 3.23093 18 3.59557 18 4.00003L18 6.00003L11 6.00003C10.4477 6.00003 10 6.44775 10 7.00003L10 9.00003C10 9.55232 10.4477 10 11 10L18 10L18 12C18 12.4045 18.2436 12.7691 18.6173 12.9239C18.991 13.0787 19.4211 12.9931 19.7071 12.7071L23.7071 8.70714Z" />
                                <path d="M14 15C14 14.4477 13.5523 14 13 14L6 14L6 12C6 11.5956 5.75636 11.2309 5.38268 11.0761C5.00901 10.9214 4.57889 11.0069 4.29289 11.2929L0.292892 15.2929C-0.097632 15.6834 -0.097632 16.3166 0.292892 16.7071L4.29289 20.7071C4.57889 20.9931 5.00901 21.0787 5.38268 20.9239C5.75636 20.7691 6 20.4045 6 20L6 18L13 18C13.5523 18 14 17.5523 14 17L14 15Z" />
                            </svg>
                        </button>
                    </div>

                    {/* Divider between From and To on mobile */}
                    <div className="border-t border-[#d9dee5] md:hidden" />

                    {/* To */}
                    <LocationCombobox
                        label="To"
                        value={state.to}
                        onChange={(value, placeId) => {
                            setState((s) => ({ ...s, to: value, toId: placeId }))
                            setErrors((e) => ({ ...e, to: false }))
                            if (placeId) {
                                tracking.trackPredictionSelected('to', value)
                            }
                        }}
                        placeholder="City or station"
                        hasError={errors.to}
                        grouped
                    />

                    {/* Divider between To and dates on mobile */}
                    <div className="border-t border-[#d9dee5] md:hidden" />

                    {/* Date section — inside mobile card, own border on desktop */}
                    {isOneWay ? (
                        <div
                            className={cn(
                                'md:overflow-hidden md:rounded-[7px] md:border',
                                errors.departDate ? 'md:border-red-500' : 'md:border-[#d9dee5]',
                            )}
                        >
                            <SearchDatePicker
                                label="Depart"
                                value={state.departDate}
                                hasError={errors.departDate}
                                onChange={(date) => {
                                    setState((s) => ({ ...s, departDate: date }))
                                    setErrors((e) => ({ ...e, departDate: false }))
                                    tracking.trackDateSelected(date)
                                }}
                                grouped
                            />
                        </div>
                    ) : (
                        <div
                            className={cn(
                                'flex flex-row items-center md:overflow-hidden md:rounded-[7px] md:border',
                                errors.departDate ? 'md:border-red-500' : 'md:border-[#d9dee5]',
                            )}
                        >
                            <div className="flex-1">
                                <SearchDatePicker
                                    label="Depart"
                                    value={state.departDate}
                                    hasError={errors.departDate}
                                    onChange={(date) => {
                                        setState((s) => ({ ...s, departDate: date }))
                                        setErrors((e) => ({ ...e, departDate: false }))
                                        tracking.trackDateSelected(date)
                                    }}
                                    grouped
                                />
                            </div>
                            <div className="h-8 w-px shrink-0 bg-[#d5dde6]" />
                            <div className="flex-1">
                                <SearchDatePicker
                                    label="Return"
                                    value={state.returnDate}
                                    onChange={(date) => {
                                        setState((s) => ({ ...s, returnDate: date }))
                                    }}
                                    placeholder="Add return"
                                    minDate={state.departDate}
                                    onTodayClick={() => {
                                        const d = new Date()
                                        const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                                        setState((s) => ({
                                            ...s,
                                            departDate: today,
                                            returnDate: today,
                                        }))
                                    }}
                                    grouped
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Search button */}
                <div className="mt-3 flex items-end md:mt-0">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="h-12 w-full rounded-[7px] bg-[#d13b3b] px-8 text-sm font-bold tracking-[2px] text-white uppercase transition-colors hover:bg-[#b83232] disabled:cursor-not-allowed disabled:opacity-70 md:w-auto"
                    >
                        {isSubmitting ? (
                            <span className="flex items-center justify-center gap-1.5">
                                <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
                                <span className="h-2 w-2 animate-pulse rounded-full bg-white [animation-delay:233ms]" />
                                <span className="h-2 w-2 animate-pulse rounded-full bg-white [animation-delay:466ms]" />
                            </span>
                        ) : (
                            'Search'
                        )}
                    </button>
                </div>
            </div>
        </form>
    )
}
